import { ApplicationType } from "@justhome/common/api/core";
import { ApplicationTypeEnum } from "@lib/api/enums";
import {
  ApplicationFormValues,
  BuildAHouseFormValues,
} from "@lib/mapApplicationData/types";
import { getApplicationProcessPath } from "@lib/routing";
import { FormStep } from "@modules/application/components/FormSteps/FormSteps";
import LeadWall from "@modules/application/components/LeadWall";
import CertificateLeadWall from "@modules/application/components/LeadWall/CertificateLeadWall";
import i18n from "i18next";
import ZeroStep from "../../components/ZeroStep";
import {
  getApplicationTypeStep,
  getConstructionCostsStep,
  getDownPaymentStep,
  getHaveFoundLandplotStep,
  getLoanAmountStep,
  getLoanDateStep,
  getMonthlyNetIncomeStep,
  getPurchasePriceStep,
  getTypeOfBuildingStep,
  getTypeOfPropertyStep,
  getWhatMarketValueStep,
  getWhereToBuyStep,
  getZipCodeStep,
} from "./commonSteps";
import { ApplicationStepId } from "./types";

type TFunction = typeof i18n.t;

interface GetApplicationProcessStepsOptions {
  partnerSlug?: string;
  locale: string;
  skipRealEstateAgentFee: boolean;
  isCertificate: boolean;
  applicationType?: ApplicationType;
}

const getApplicationProcessSteps = (
  t: TFunction,
  {
    partnerSlug,
    locale,
    skipRealEstateAgentFee,
    isCertificate,
    applicationType,
  }: GetApplicationProcessStepsOptions,
): FormStep<ApplicationFormValues>[] => {
  const otherPath = getApplicationProcessPath(
    locale,
    "other",
    undefined,
    partnerSlug,
  );

  const steps: FormStep<ApplicationFormValues>[] = [];

  if (!applicationType) {
    steps.push({
      id: ApplicationStepId.applicationType,
      component: ZeroStep,
      config: getApplicationTypeStep(t, otherPath),
      possibleTransitions: [
        ApplicationStepId.buyWithSavingsTypeOfProperty,
        ApplicationStepId.buildAHouseHaveFoundLandplot,
        ApplicationStepId.refinancingWhereIsProperty,
      ],
      transition: (value: any) => {
        switch (value.applicationType) {
          case ApplicationTypeEnum.BUY_WITH_SAVINGS:
            return ApplicationStepId.buyWithSavingsTypeOfProperty;
          case ApplicationTypeEnum.BUILD_HOUSE:
            return ApplicationStepId.buildAHouseHaveFoundLandplot;
          case ApplicationTypeEnum.REFINANCING:
          default:
            return ApplicationStepId.refinancingWhereIsProperty;
        }
      },
    });
  }

  if (
    !applicationType ||
    applicationType === ApplicationTypeEnum.BUY_WITH_SAVINGS
  ) {
    steps.push({
      id: ApplicationStepId.buyWithSavingsTypeOfProperty,
      label: t("step_property_type"),
      config: getTypeOfPropertyStep(t, otherPath),
      transition: ApplicationStepId.buyWithSavingsWhereToBuy,
    });

    steps.push({
      id: ApplicationStepId.buyWithSavingsWhereToBuy,
      label: t("step_zip_code"),
      config: getWhereToBuyStep(t),
      transition: ApplicationStepId.buyWithSavingsPurchasePrice,
    });

    steps.push({
      id: ApplicationStepId.buyWithSavingsPurchasePrice,
      label: t("step_purchase_price"),
      config: getPurchasePriceStep(t, skipRealEstateAgentFee),
      transition: ApplicationStepId.buyWithSavingsDownPayment,
    });

    steps.push({
      id: ApplicationStepId.buyWithSavingsDownPayment,
      label: t("step_down_payment"),
      config: getDownPaymentStep(t),
      transition: ApplicationStepId.buyWithSavingsMonthlyNetIncome,
    });

    steps.push({
      id: ApplicationStepId.buyWithSavingsMonthlyNetIncome,
      label: t("step_net_income"),
      config: getMonthlyNetIncomeStep(t),
      transition: ApplicationStepId.leadWall,
    });
  }

  if (!applicationType || applicationType === ApplicationTypeEnum.BUILD_HOUSE) {
    steps.push({
      id: ApplicationStepId.buildAHouseHaveFoundLandplot,
      label: t("step_land_plot"),
      config: getHaveFoundLandplotStep(t),
      possibleTransitions: [
        ApplicationStepId.buildAHouseWhereLandplotOwner,
        ApplicationStepId.buildAHouseWhereLandplot,
      ],
      transition: (value) =>
        (value as BuildAHouseFormValues).ownsLandplot
          ? ApplicationStepId.buildAHouseWhereLandplotOwner
          : ApplicationStepId.buildAHouseWhereLandplot,
    });

    steps.push({
      id: ApplicationStepId.buildAHousePurchasePrice,
      label: t("step_purchase_price"),
      config: getPurchasePriceStep(t, skipRealEstateAgentFee),
      transition: ApplicationStepId.buildAHouseTypeOfProperty,
    });

    steps.push({
      id: ApplicationStepId.buildAHouseWhereLandplotOwner,
      label: t("step_zip_code"),
      config: getZipCodeStep(
        t,
        "zip_code_landplot_owned_title",
        "zip_code_landplot_label",
      ),
      transition: ApplicationStepId.buildAHouseTypeOfProperty,
    });

    steps.push({
      id: ApplicationStepId.buildAHouseWhereLandplot,
      label: t("step_zip_code"),
      config: getZipCodeStep(
        t,
        "zip_code_landplot_title",
        "zip_code_landplot_label",
      ),
      transition: ApplicationStepId.buildAHousePurchasePrice,
    });

    steps.push({
      id: ApplicationStepId.buildAHouseTypeOfProperty,
      label: t("step_property_type"),
      config: getTypeOfBuildingStep(t),
      transition: ApplicationStepId.buildAHouseConstructionCosts,
    });

    steps.push({
      id: ApplicationStepId.buildAHouseConstructionCosts,
      label: t("step_construction_cost"),
      config: getConstructionCostsStep(t),
      transition: ApplicationStepId.buildAHouseDownPayment,
    });

    steps.push({
      id: ApplicationStepId.buildAHouseDownPayment,
      label: t("step_down_payment"),
      config: getDownPaymentStep(t),
      transition: ApplicationStepId.buildAHouseMonthlyNetIncome,
    });

    steps.push({
      id: ApplicationStepId.buildAHouseMonthlyNetIncome,
      label: t("step_net_income"),
      config: getMonthlyNetIncomeStep(t),
      transition: ApplicationStepId.leadWall,
    });
  }

  if (!applicationType || applicationType === ApplicationTypeEnum.REFINANCING) {
    steps.push({
      id: ApplicationStepId.refinancingWhereIsProperty,
      label: t("step_zip_code"),
      config: getZipCodeStep(t, "zip_code_title_refinancing"),
      transition: ApplicationStepId.refinancingWhatMarketValue,
    });

    steps.push({
      id: ApplicationStepId.refinancingWhatMarketValue,
      label: t("step_market_value"),
      config: getWhatMarketValueStep(t),
      transition: ApplicationStepId.refinancingLoanAmount,
    });

    steps.push({
      id: ApplicationStepId.refinancingLoanAmount,
      label: t("step_loan_amount"),
      config: getLoanAmountStep(t),
      transition: ApplicationStepId.refinancingLoanDate,
    });

    steps.push({
      id: ApplicationStepId.refinancingLoanDate,
      label: t("step_loan_date"),
      config: getLoanDateStep(t),
      transition: ApplicationStepId.refinancingMonthlyNetIncome,
    });

    steps.push({
      id: ApplicationStepId.refinancingMonthlyNetIncome,
      label: t("step_net_income"),
      config: getMonthlyNetIncomeStep(t),
      transition: ApplicationStepId.leadWall,
    });
  }

  steps.push({
    id: ApplicationStepId.leadWall,
    label: t("step_lead_wall"),
    config: {},
    component: isCertificate ? CertificateLeadWall : LeadWall,
  });

  return steps;
};

export default getApplicationProcessSteps;
